import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { COLORS } from '../theme';

function Skeleton({ width = '80%', height = '14px' }) {
  return (
    <div style={{
      width,
      height,
      backgroundColor: COLORS.surfaceHover,
      borderRadius: '4px',
      animation: 'pulse 1.4s ease-in-out infinite',
    }} />
  );
}

function StatCard({ label, value, loading }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      padding: '24px',
    }}>
      <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '32px', fontWeight: '700', color: COLORS.textPrimary, lineHeight: 1 }}>
        {loading ? <Skeleton width="80px" height="32px" /> : value}
      </div>
      <div style={{ height: '3px', width: '40px', backgroundColor: COLORS.accent, borderRadius: '2px', marginTop: '12px' }} />
    </div>
  );
}

const COL = '160px 1fr 1fr 120px 100px';

export default function RevenuePage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubs = [];

    unsubs.push(onSnapshot(
      query(collection(db, 'payments'), where('status', '==', 'succeeded'), orderBy('createdAt', 'desc'), limit(50)),
      snap => {
        setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      _err => {
        setLoading(false);
      }
    ));

    return () => unsubs.forEach(u => u());
  }, []);

  const totalRevenue = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const hireFees = payments.filter(p => p.type !== 'i9').reduce((sum, p) => sum + (p.amount || 0), 0);
  const i9Fees = payments.filter(p => p.type === 'i9').reduce((sum, p) => sum + (p.amount || 0), 0);

  const fmt = (cents) => '$' + Math.floor(cents / 100).toLocaleString('en-US');
  const fmtDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const headerCell = { fontSize: '12px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0 0 12px 0', fontWeight: '600' };
  const cell = { padding: '12px 0', fontSize: '13px', color: COLORS.textPrimary };

  return (
    <>
      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <StatCard label="Total Revenue" value={fmt(totalRevenue)} loading={loading} />
        <StatCard label="Hire Fees" value={fmt(hireFees)} loading={loading} />
        <StatCard label="I-9 Fees" value={fmt(i9Fees)} loading={loading} />
      </div>

      {/* Payments table */}
      <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '0 20px', borderBottom: `1px solid ${COLORS.border}` }}>
          {['Date', 'Employer', 'Job', 'Type', 'Amount'].map(h => (
            <div key={h} style={headerCell}>{h}</div>
          ))}
        </div>

        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: COL, padding: '14px 20px', borderBottom: `1px solid ${COLORS.border}30` }}>
              {Array.from({ length: 5 }).map((__, j) => <div key={j}><Skeleton /></div>)}
            </div>
          ))
        ) : payments.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '13px' }}>
            No payments recorded yet.
          </div>
        ) : (
          payments.map((p, i) => (
            <div key={p.id} style={{
              display: 'grid',
              gridTemplateColumns: COL,
              padding: '0 20px',
              borderBottom: i < payments.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
              alignItems: 'center',
            }}>
              <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '12px' }}>{fmtDate(p.createdAt)}</div>
              <div style={cell}>{p.employerName || p.employerId || '—'}</div>
              <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '12px' }}>{p.jobTitle || p.jobId || '—'}</div>
              <div style={cell}>
                <span style={{
                  backgroundColor: p.type === 'i9' ? '#F5A62326' : '#34A85326',
                  color: p.type === 'i9' ? COLORS.warning : COLORS.success,
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}>
                  {p.type === 'i9' ? 'I-9' : 'Hire Fee'}
                </span>
              </div>
              <div style={{ ...cell, fontWeight: '600', color: COLORS.success }}>{fmt(p.amount || 0)}</div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
